import React from 'react';

import { useTranslation } from '@lyearn/i18n';
import { Edit2Outline } from '@lyearn/icons';
import { Input, Stack } from '@lyearn/ui';
import UserSearch from '@/components/UsersSearch';
import { UserFragment } from '@/graphql/fragments/generated/User';

import { TimeIntervalSelector } from '../../components/TimeIntervalSelector';
import UserSearchAdornment from '../../components/UserSearchAdornment';

type IntervalViewProps = {
  rawTimeInput: string;
  setRawTimeInput: (value: string) => void;
  startTime: Date;
  endTime: Date;
  handleStartDateChange: (date: Date | null | undefined) => void;
  handleEndDateChange: (date: Date | null | undefined) => void;
  handleStartTimeSelect: (dateString: string) => void;
  handleEndTimeSelect: (dateString: string) => void;
  instructions: string;
  setInstructions: (value: string) => void;
  selectedUserId: string | undefined;
  setSelectedUserId: (value: string | undefined) => void;
  user: UserFragment | undefined;
  fetching: boolean;
  isEditMode?: boolean;
};

export const IntervalView: React.FC<IntervalViewProps> = ({
  rawTimeInput,
  setRawTimeInput,
  startTime,
  endTime,
  handleStartDateChange,
  handleEndDateChange,
  handleStartTimeSelect,
  handleEndTimeSelect,
  instructions,
  setInstructions,
  selectedUserId,
  setSelectedUserId,
  user,
  fetching,
  isEditMode = false,
}) => {
  const { t } = useTranslation(['Task']);

  return (
    <Stack className="mt-24 rounded-12" direction="col">
      <Stack className="w-full">
        {isEditMode ? null : (
          <UserSearch
            className="w-full border-none"
            initalValue={user?.name}
            placeholder={t('Task:TimerMenu.SelectUserPlaceholder')}
            renderStartAdornment={() => (
              <UserSearchAdornment
                fetching={fetching}
                selectedUserId={selectedUserId}
                user={user}
              />
            )}
            onChange={setSelectedUserId}
          />
        )}
      </Stack>
      <Stack className="my-16">
        <Input
          fullWidth
          placeholder={t('Task:TimerMenu.TimeInputPlaceholder')}
          size="large"
          value={rawTimeInput}
          onChange={(e) => setRawTimeInput(e.target.value)}
        />
      </Stack>
      <TimeIntervalSelector
        endTime={endTime}
        handleEndDateChange={handleEndDateChange}
        handleEndTimeSelect={handleEndTimeSelect}
        handleStartDateChange={handleStartDateChange}
        handleStartTimeSelect={handleStartTimeSelect}
        isEditMode={isEditMode}
        startTime={startTime}
      />
      <Stack className="mt-16">
        <Input
          fullWidth
          multiline
          placeholder={t('Task:TimerMenu.NotesPlaceholder')}
          size="large"
          startAdornment={<Edit2Outline fillColorToken="icon-secondary" />}
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
        />
      </Stack>
    </Stack>
  );
};
